// The framework-free half of the hire-return widgets (sprint 16): turning the provider's rows into what the
// card draws. Kept apart from the DOM so vitest can pin it, the equipmentCheckModel shape.

import { holdingLabel } from "../EquipmentCheckReminders/equipmentCheckModel";

export interface HireReturnRow {
    LineId: number;
    AgreementId?: number | null;
    AgreementName?: string | null;
    HireReference?: string | null;
    SupplierName?: string | null;
    ItemId: number;
    ItemName?: string | null;
    AssetTag?: string | null;
    OnHireDate?: string | null;
    ExpectedOffHireDate?: string | null;
    /** "Overdue", "DueSoon" or "NoDate" - the server's HireReturnStandingEnum member name. */
    Standing?: string | null;
    DaysUntilDue?: number | null;
    ProjectId?: number | null;
    ProjectNumber?: string | null;
    /** "Company / BU" when the item is held centrally (sprint 23). */
    CentralName?: string | null;
    Location?: string | null;
    ResponsibleContactId?: number | null;
    ResponsibleContactName?: string | null;
}

export interface HireReturnConfig {
    Title?: string | null;
    ShowProject?: boolean | null;
    ShowResponsible?: boolean | null;
    EmptyText?: string | null;
}

export const DefaultTitle = "Hired items due back";
export const DefaultEmptyText = "Nothing due back";
export { CentralLabel, UnallocatedLabel } from "../EquipmentCheckReminders/equipmentCheckModel";

/** Accepts the provider's array or a { Items } envelope; anything else is no rows. */
export function toRows(data: unknown): HireReturnRow[] {
    if (Array.isArray(data))
        return data.filter(r => !!r) as HireReturnRow[];

    const items = (data as { Items?: unknown } | null | undefined)?.Items;
    return Array.isArray(items) ? items.filter(r => !!r) as HireReturnRow[] : [];
}

export function isOverdue(row: HireReturnRow): boolean {
    return row?.Standing === "Overdue";
}

export function titleLabel(config: HireReturnConfig | null | undefined): string {
    return config?.Title?.trim() || DefaultTitle;
}

export function emptyText(config: HireReturnConfig | null | undefined): string {
    return config?.EmptyText?.trim() || DefaultEmptyText;
}

/** "Tower light (TL-4) — from Acme Hire". */
export function rowLabel(row: HireReturnRow): string {
    const item = row?.ItemName?.trim() || `Item ${row?.ItemId ?? "?"}`;
    const tag = row?.AssetTag?.trim();
    const supplier = row?.SupplierName?.trim();
    const named = tag ? `${item} (${tag})` : item;
    return supplier ? `${named} — from ${supplier}` : named;
}

/** Where the item is now: the project and, when given, the location on it. */
export function whereLabel(row: HireReturnRow): string {
    const project = holdingLabel(row?.ProjectNumber, row?.CentralName);
    const location = row?.Location?.trim();
    return location ? `${project} · ${location}` : project;
}

/** What the right-hand column says. */
export function standingLabel(row: HireReturnRow): string {
    if (row?.Standing === "NoDate") return "On hire, no return date";

    const days = row?.DaysUntilDue;
    if (typeof days !== "number") return row?.Standing === "Overdue" ? "Overdue" : "Due back";

    if (days < 0) return days === -1 ? "Overdue by 1 day" : `Overdue by ${-days} days`;
    if (days === 0) return "Due back today";
    if (days === 1) return "Due back tomorrow";
    return `Due back in ${days} days`;
}

export interface HireReturnView {
    total: number;
    overdue: number;
    rows: HireReturnRow[];
}

export function buildView(data: unknown): HireReturnView {
    const rows = toRows(data);
    return { total: rows.length, overdue: rows.filter(isOverdue).length, rows };
}
